// ==========================================================================
// DASHBOARD & EXECUTIVE SUMMARY MODULE
// WuRuPayroll - Monthly metrics, employee summary cards, bulk lock & confirm
// ==========================================================================

import {
  getTimesheet,
  saveTimesheet,
  getStorageUsage
} from '../firebase-db.js';

import { escapeHtml, escapeAttr, escapeJsParam } from '../utils/sanitize.js';
import { toast, showConfirm } from '../utils/toast.js';
import {
  isEmployeeActiveInMonth,
  getEmployeeWorkHoursStr,
  calculatePayrollForEmployee,
  finalizeTimesheetDays
} from './payroll-calc.js';
import {
  openDailyRecordModal,
  openEmployeeTimesheetPage
} from './timesheets.js';

// Safe proxy for global application state
const state = new Proxy({}, {
  get(target, prop) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    return s[prop];
  },
  set(target, prop, value) {
    const s = (typeof window !== 'undefined' && window.state) ? window.state : (globalThis.state || target);
    s[prop] = value;
    return true;
  }
});

// Safe wrappers for app routing, context and permissions
const navigateTo = (page) => {
  if (typeof window !== 'undefined' && window.navigateTo) {
    return window.navigateTo(page);
  }
};

const checkPermission = (action) => {
  if (typeof window !== 'undefined' && window.checkPermission) {
    return window.checkPermission(action);
  }
  return true;
};

const canViewSalary = (emp) => {
  if (typeof window !== 'undefined' && window.canViewSalary) {
    return window.canViewSalary(emp);
  }
  return true;
};

async function autoConfirmAllEmployeesTimesheets (isSilent = true) {
  const timesheetMap = new Map();
  try {
    const activeEmployees = state.employees.filter(e => isEmployeeActiveInMonth(e, state.selectedMonth));
    const today = new Date();
    const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const [yearStr, monthStr] = state.selectedMonth.split('-');
    const year = parseInt(yearStr);
    const m = parseInt(monthStr);
    const daysInMonth = new Date(year, m, 0).getDate();
    const dayOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

    const confirmPromises = activeEmployees.map(async (emp) => {
      const ts = await getTimesheet(emp.id, state.selectedMonth);
      let wasUpdated = false;
      if (!ts.isLocked) {
        let changed = false;
        if (!ts.days) {
          ts.days = {};
          changed = true;
        }

        for (let d = 1; d <= daysInMonth; d++) {
          const dateObj = new Date(year, m - 1, d);
          const dateString = `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          const dayName = dayOfWeekNames[dateObj.getDay()];

          let isDefaultDayOff = false;
          if (emp.weeklyDaysOff !== undefined && emp.weeklyDaysOff.length > 0) {
            isDefaultDayOff = emp.weeklyDaysOff.includes(dateObj.getDay());
          } else {
            const workHoursStr = window.getEmployeeWorkHoursStr(emp, state.activeCompanySettings, dayName);
            isDefaultDayOff = workHoursStr === '0' || workHoursStr.startsWith('OFF:');
          }

          const isCompanyHoliday = state.holidays && state.holidays.some(h => h.date === dateString);
          const isSpecialWorkday = state.specialWorkdays && state.specialWorkdays.some(s => s.date === dateString);
          const isCompensatoryHoliday = state.specialWorkdays && state.specialWorkdays.some(s => s.compensatoryOffDate === dateString);

          let detectedStatus = 'work';
          if (emp.startDate && dateString < emp.startDate) detectedStatus = 'not_started';
          else if (emp.resignDate && dateString > emp.resignDate) detectedStatus = 'resigned';
          else if (isSpecialWorkday) detectedStatus = 'special_workday';
          else if (isCompensatoryHoliday) detectedStatus = 'compensatory_holiday';
          else if (isCompanyHoliday) detectedStatus = 'holiday';
          else if (isDefaultDayOff) detectedStatus = 'day_off';

          if (!ts.days[d]) {
            const dayStatus = dateString > todayString ? 'not_started' : detectedStatus;
            ts.days[d] = {
              status: dayStatus,
              lateMinutes: 0,
              earlyLeaveMinutes: 0,
              advance: 0
            };
            changed = true;
          } else {
            const dayData = ts.days[d];
            const isUntouched = !dayData.timeIn && !dayData.timeOut && !dayData.advance && (dayData.overrideAmount === undefined || dayData.overrideAmount === null || dayData.overrideAmount === '');

            if (dateString <= todayString) {
              if (dayData.status === 'not_started' && !(emp.startDate && dateString < emp.startDate)) {
                dayData.status = detectedStatus;
                changed = true;
              } else if (isUntouched && dayData.status === 'work' && detectedStatus !== 'work') {
                dayData.status = detectedStatus;
                changed = true;
              }
            } else {
              // Future date: ensure untouched days default to not_started
              if (isUntouched && dayData.status !== 'not_started' && dayData.status !== 'resigned') {
                dayData.status = 'not_started';
                changed = true;
              }
            }
          }
        }

        const newSummary = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);
        const summaryChanged = !ts.summary || ts.summary.netSalary !== newSummary.netSalary || ts.summary.basicSalaryEarned !== newSummary.basicSalaryEarned;
        if (ts.isPhantom || changed || summaryChanged) {
          ts.isPhantom = false;
          ts.isLocked = false;
          ts.summary = newSummary;
          await saveTimesheet(emp.id, state.selectedMonth, ts);
          wasUpdated = true;
        }
      }
      timesheetMap.set(emp.id, ts);
      return wasUpdated;
    });

    const confirmResults = await Promise.all(confirmPromises);
    const updatedCount = confirmResults.filter(Boolean).length;
    if (updatedCount > 0 && !isSilent) {
      console.log(`Auto-confirmed attendance for ${updatedCount} employees.`);
    }
  } catch (err) {
    console.error("Auto-confirm failed:", err);
  }
  return timesheetMap;
};

async function triggerLockAllTimesheets (isAuto = false) {
  if (!isAuto && !confirm("คุณต้องการบันทึกและล็อคข้อมูลเวลาทำงานของพนักงานทุกคนในเดือนนี้ใช่หรือไม่?\n\nเมื่อยืนยันแล้ว ข้อมูลจะถูกบันทึกถาวรลงระบบเพื่อป้องกันการคลาดเคลื่อนในอนาคต")) return;

  try {
    if (!isAuto) {
      const banner = document.getElementById('dash-warning-banner-container');
      if (banner) banner.innerHTML = `<div class="glass-card text-center" style="margin-bottom:20px;">กำลังบันทึกข้อมูลทุกคน โปรดรอสักครู่...</div>`;
    }

    const activeEmployees = state.employees.filter(e => isEmployeeActiveInMonth(e, state.selectedMonth));

    const lockPromises = activeEmployees.map(async (emp) => {
      const ts = await getTimesheet(emp.id, state.selectedMonth);
      if (!ts.isLocked) {
        finalizeTimesheetDays(emp, ts, state.selectedMonth);
        ts.isPhantom = false;
        ts.isLocked = true;
        delete ts.summary;
        ts.summary = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);
        await saveTimesheet(emp.id, state.selectedMonth, ts);

        // Update employee profile if security deposit was deducted in this month (Requirement 5)
        if (emp.requiresSecurityDeposit && emp.securityDepositStatus === 'none') {
          const startMonth = emp.startDate ? emp.startDate.slice(0, 7) : '';
          if (state.selectedMonth === startMonth) {
            await updateEmployee(emp.id, { securityDepositStatus: 'deducted' });
          }
        }

        // Update employee profile if security deposit was refunded in this month (Requirement 5)
        if (emp.requiresSecurityDeposit && emp.securityDepositStatus === 'deducted' && emp.status === 'resigned' && emp.resignedDate) {
          const resignationMonth = emp.resignedDate ? emp.resignedDate.slice(0, 7) : '';
          if (state.selectedMonth === resignationMonth) {
            await updateEmployee(emp.id, { securityDepositStatus: 'refunded' });
          }
        }

        return true;
      }
      return false;
    });

    const lockResults = await Promise.all(lockPromises);
    const count = lockResults.filter(Boolean).length;

    if (!isAuto) toast.success(`บันทึกและล็อคข้อมูลเรียบร้อยแล้ว จำนวน ${count} รายการ!`);
    renderDashboard();
  } catch (err) {
    console.error(err);
    if (!isAuto) toast.error("เกิดข้อผิดพลาดในการบันทึกข้อมูล: " + (err.message || ''));
  }
};

// Dashboard View
function renderDashboard() {
  document.getElementById('page-display-title').textContent = 'หน้าหลัก (Dashboard)';
  document.getElementById('page-display-desc').textContent = `ภาพรวมระบบเงินเดือนประจำงวดเดือน ${state.selectedMonth} ของบริษัท ${state.activeCompanySettings.name}`;

  const wrapper = document.getElementById('page-content-wrapper');

  // Compute overall stats for active month
  let totalGross = 0;
  let totalNet = 0;
  let totalSS = 0;
  let totalTax = 0;
  let totalAdvance = 0;
  let activeEmpsArray = state.employees.filter(e => isEmployeeActiveInMonth(e, state.selectedMonth));
  if (!window.checkPermission('view_employees')) {
    activeEmpsArray = activeEmpsArray.filter(e => e.id === state.currentUser.id);
  }
  let activeEmps = activeEmpsArray.length;

  // Let's load the timesheets asynchronously and update stats
  const fetchAndSumStats = async () => {
    // Auto-confirm attendance up to today for unlocked records on entering/rendering
    const timesheetMap = await autoConfirmAllEmployeesTimesheets();

    let htmlContent = `
      <div id="dash-warning-banner-container"></div>
      ${window.checkPermission('view_employees') ? `
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 20px; margin-bottom: 25px;">
        <div class="glass-card stat-card">
          <div class="stat-icon">
            <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
          </div>
          <div>
            <div class="stat-number" id="dash-active-count">${activeEmps} คน</div>
            <div class="stat-label">พนักงานทำงานอยู่ทั้งหมด</div>
          </div>
        </div>
        
        <div class="glass-card stat-card">
          <div class="stat-icon" style="color: var(--success);">
            <svg viewBox="0 0 24 24"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 1.04-.9 1.77-2.5 1.77-1.91 0-2.63-.99-2.7-2.3H5.21c.06 2.22 1.78 3.48 3.79 3.97V21h3v-2.15c2.04-.37 3.5-1.5 3.5-3.51 0-2.58-2.2-3.55-3.7-3.84z"/></svg>
          </div>
          <div>
            <div class="stat-number" id="dash-total-payout">... บาท</div>
            <div class="stat-label">ยอดจ่ายเงินสุทธิเดือนนี้</div>
          </div>
        </div>

        <div class="glass-card stat-card">
          <div class="stat-icon" style="color: var(--info);">
            <svg viewBox="0 0 24 24"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z"/></svg>
          </div>
          <div>
            <div class="stat-number" id="dash-total-ss">... บาท</div>
            <div class="stat-label">ยอดส่งประกันสังคมเดือนนี้</div>
          </div>
        </div>

        <div class="glass-card stat-card" style="display: flex; flex-direction: column; align-items: stretch; gap: 10px; padding: 22px 30px;">
          <div style="display: flex; align-items: center; gap: 15px;">
            <div class="stat-icon" style="color: var(--warning); width: 45px; height: 45px; flex-shrink: 0;">
              <svg viewBox="0 0 24 24"><path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM19 18H6c-2.21 0-4-1.79-4-4 0-2.05 1.53-3.76 3.56-3.97l1.07-.11.5-.95C8.08 7.14 9.94 6 12 6c2.62 0 4.88 1.86 5.39 4.43l.3 1.5 1.53.11c1.56.1 2.78 1.41 2.78 2.96 0 1.65-1.35 3-3 3z"/></svg>
            </div>
            <div style="flex-grow: 1;">
              <div class="stat-number" id="dash-storage-percentage" style="font-size: 1.4rem; line-height: 1.2;">...%</div>
              <div class="stat-label" style="font-size: 0.8rem;">พื้นที่คลังข้อมูล (จำกัด 4.8 GB)</div>
            </div>
          </div>
          <div style="width: 100%; background: var(--gray-300); height: 8px; border-radius: 4px; overflow: hidden; position: relative;">
            <div id="dash-storage-progress-bar" style="width: 0%; background: linear-gradient(90deg, var(--warning), var(--danger)); height: 100%; transition: width 0.5s ease;"></div>
          </div>
          <div style="display: flex; justify-content: space-between; font-size: 0.72rem; color: var(--text-sub); font-weight: 500;">
            <span id="dash-storage-used">... MB</span>
            <span>4.8 GB</span>
          </div>
        </div>
      </div>
      ` : ''}


      <div class="glass-card">
        <h3 style="font-weight: 700; margin-bottom: 20px; color: var(--primary-dark);">ตารางสถานะการคำนวณเงินเดือนพนักงาน</h3>
        <div class="table-responsive">
          <table class="payroll-table">
            <thead>
              <tr>
                <th>พนักงาน</th>
                <th>ประเภท</th>
                <th>เงินได้</th>
                ${state.activeCompanySettings.enableDiligenceAllowance !== false ? '<th>เบี้ยขยัน</th>' : ''}
                <th>หักประกันสังคม</th>
                <th>หัก ณ ที่จ่าย</th>
                <th>เบิกล่วงหน้า</th>
                <th>ยอดหักอื่นๆ</th>
                <th>รับสุทธิ</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody id="dash-payroll-rows">
              <tr><td colspan="${state.activeCompanySettings.enableDiligenceAllowance !== false ? 10 : 9}" class="text-center">กำลังดึงข้อมูล...</td></tr>
            </tbody>
          </table>
        </div>
      </div>

    `;

    wrapper.innerHTML = htmlContent;

    // Load sheets & compute summary
    const rowsBody = document.getElementById('dash-payroll-rows');
    if (!rowsBody) return;

    let rowsHTML = '';
    const activeEmployees = activeEmpsArray;

    if (activeEmployees.length === 0) {
      rowsBody.innerHTML = `<tr><td colspan="${state.activeCompanySettings.enableDiligenceAllowance !== false ? 10 : 9}" class="text-center" style="color: var(--text-sub);">ไม่พบพนักงานในระบบของบริษัทนี้</td></tr>`;
      document.getElementById('dash-total-payout').textContent = "0.00 บาท";
      document.getElementById('dash-total-ss').textContent = "0.00 บาท";
      return;
    }

    const today = new Date();
    const isLastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate() === today.getDate();
    const currentMonthStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
    const isSelectedCurrent = state.selectedMonth === currentMonthStr;
    const isSelectedPast = state.selectedMonth < currentMonthStr;

    let hasUnsaved = false;
    const timesheetResults = await Promise.all(activeEmployees.map(async (emp) => {
      let ts = timesheetMap ? timesheetMap.get(emp.id) : null;
      if (!ts) {
        ts = await getTimesheet(emp.id, state.selectedMonth);
      }
      return { emp, ts };
    }));

    for (const { emp, ts } of timesheetResults) {
      if (!ts.isLocked) {
        hasUnsaved = true;
      }
      let summ;
      if (ts.isLocked && ts.summary && ts.summary.netSalary > 0) {
        summ = ts.summary;
      } else {
        summ = calculatePayrollForEmployee(emp, ts, state.activeCompanySettings);
      }

      const canView = window.canViewSalary(emp);

      if (canView) {
        totalGross += summ.basicSalaryEarned + summ.diligenceAllowance + summ.otherIncomes;
        totalNet += summ.netSalary;
        totalSS += summ.socialSecurityDeducted;
        totalTax += summ.withholdingTaxDeducted;
        totalAdvance += summ.totalAdvance;
      }

      const typeLabel = emp.type === 'salary' ? 'เงินเดือน' : (emp.type === 'daily' ? 'รายวัน' : 'ฟรีแลนซ์');
      const statusBadge = ts.isLocked ?
        `<span class="badge badge-success">จ่ายแล้ว (ล็อคแล้ว)</span>` :
        `<span class="badge badge-warning">รอยืนยัน (ยังไม่ล็อค)</span>`;

      const otherDeductions = summ.lateDeduction + summ.studentLoanDeducted + summ.securityDepositDeducted + summ.otherDeductions;

      rowsHTML += `
        <tr>
          <td>
            <div style="display: flex; flex-direction: column; gap: 6px; align-items: flex-start;">
              ${window.checkPermission('edit_timesheet') ? 
                `<div onclick="openEmployeeTimesheetPage('${escapeJsParam(emp.id)}')" style="cursor: pointer; color: var(--primary);" title="คลิกเพื่อไปยังหน้าบันทึกเวลาทำงานรายวัน"><strong>${escapeHtml(emp.realName)}</strong> (${escapeHtml(emp.nickname)})</div>` : 
                `<div style="color: var(--text-main);"><strong>${escapeHtml(emp.realName)}</strong> (${escapeHtml(emp.nickname)})</div>`
              }
              ${window.checkPermission('edit_timesheet') ? 
                `<button class="btn btn-warning btn-sm" style="display: flex; align-items: center; justify-content: center; gap: 4px; background:var(--accent); color:white; font-weight:700; padding: 4px 10px; font-size: 0.75rem; border-radius: 4px; border: none; cursor: pointer;" onclick="openDailyRecordModal('${escapeJsParam(emp.id)}')">⚡ บันทึกด่วนรายวัน</button>` : ''
              }
            </div>
          </td>
          <td>${typeLabel}</td>
          <td>${canView ? (summ.basicSalaryEarned + summ.diligenceAllowance + summ.otherIncomes).toLocaleString() + ' บาท' : '****'}</td>
          ${state.activeCompanySettings.enableDiligenceAllowance !== false ? `<td>${canView ? (summ.diligenceAllowance > 0 ? `<span style="color: var(--success); font-weight:700;">+${summ.diligenceAllowance}</span>` : '0') : '****'}</td>` : ''}
          <td><span style="color: var(--danger);">${canView ? (summ.socialSecurityDeducted > 0 ? summ.socialSecurityDeducted.toLocaleString() : '0') : '****'}</span></td>
          <td><span style="color: var(--danger);">${canView ? (summ.withholdingTaxDeducted > 0 ? summ.withholdingTaxDeducted.toLocaleString() : '0') : '****'}</span></td>
          <td><span style="color: var(--danger);">${canView ? (summ.totalAdvance > 0 ? summ.totalAdvance.toLocaleString() : '0') : '****'}</span></td>
          <td><span style="color: var(--danger);">${canView ? (otherDeductions > 0 ? otherDeductions.toLocaleString() : '0') : '****'}</span></td>
          <td><strong>${canView ? (summ.netSalary).toLocaleString() + ' บาท' : '****'}</strong></td>
          <td>${statusBadge}</td>
        </tr>
      `;
    }

    rowsBody.innerHTML = rowsHTML;
    if (document.getElementById('dash-total-payout')) {
      document.getElementById('dash-total-payout').textContent = `${totalNet.toLocaleString()} บาท`;
      document.getElementById('dash-total-ss').textContent = `${totalSS.toLocaleString()} บาท`;
    }

    const bannerContainer = document.getElementById('dash-warning-banner-container');
    if (bannerContainer) {
      if (hasUnsaved && (isSelectedCurrent || isSelectedPast) && window.checkPermission('lock_timesheet')) {
        const isEndPeriod = (isLastDay && isSelectedCurrent) || isSelectedPast;
        const bannerTitle = isEndPeriod ? '⚠️ วันนี้เป็นวันสิ้นเดือน! ยังมีข้อมูลเงินเดือนที่ยังไม่ได้ทำการล็อค (Lock)' : '⚠️ ยังมีข้อมูลเงินเดือนที่ยังไม่ได้ทำการล็อค (Lock)';
        const bannerDesc = isEndPeriod ?
          'ระบบยืนยันสถานะเวลาทำงานแบบ Auto ครบ 31 วันแล้ว กรุณาตรวจสอบความถูกต้องและกดปุ่ม <strong>"🔒 ยืนยันและล็อคข้อมูลทั้งบริษัท"</strong> เพื่อล็อคข้อมูลเงินเดือนให้เรียบร้อยก่อนปิดงวดและออกสลิปเงินเดือน' :
          'ระบบยืนยันสถานะเวลาทำงานแบบ Auto เรียบร้อยแล้ว แต่ยังไม่ได้ทำการล็อคข้อมูลถาวร โปรดกดยืนยันและล็อคข้อมูลเมื่อพร้อมปิดงวดเดือนนี้';

        bannerContainer.innerHTML = `
          <div style="background: #fff8e1; border: 1px solid #ffc107; padding: 15px 20px; border-radius: var(--border-radius-md); margin-bottom: 25px; display: flex; align-items: center; justify-content: space-between; gap: 20px; flex-wrap: wrap;">
            <div style="display: flex; gap: 15px; align-items: center;">
              <span style="font-size: 2rem;">⚠️</span>
              <div>
                <h4 style="margin: 0 0 5px 0; color: #9c6c00; font-weight: 700;">${bannerTitle}</h4>
                <p style="margin: 0; color: #7a5500; font-size: 0.9rem;">${bannerDesc}</p>
              </div>
            </div>
            <button class="btn btn-warning" style="font-weight: 700; white-space: nowrap; color: #000; border: none; padding: 10px 20px;" onclick="triggerLockAllTimesheets()">🔒 ยืนยันและล็อคข้อมูลทั้งบริษัท</button>
          </div>
        `;
      } else {
        bannerContainer.innerHTML = '';
      }
    }



    // Fetch and show storage usage (Requirement 4.8 GB bar)
    try {
      const storageUsedBytes = await getStorageUsage();
      const limitBytes = 4.8 * 1024 * 1024 * 1024;
      const storageUsedMB = (storageUsedBytes / (1024 * 1024)).toFixed(1);
      const storagePercentage = Math.min(100, (storageUsedBytes / limitBytes) * 100).toFixed(1);

      const percentEl = document.getElementById('dash-storage-percentage');
      const usedEl = document.getElementById('dash-storage-used');
      const progressEl = document.getElementById('dash-storage-progress-bar');

      if (percentEl) percentEl.textContent = `${storagePercentage}%`;
      if (usedEl) usedEl.textContent = `${storageUsedMB} MB`;
      if (progressEl) progressEl.style.width = `${storagePercentage}%`;
    } catch (err) {
      console.error("Error fetching storage usage:", err);
    }
  };

  fetchAndSumStats();
}



// Bind dashboard functions to window for backwards compatibility
if (typeof window !== 'undefined') {
  window.autoConfirmAllEmployeesTimesheets = autoConfirmAllEmployeesTimesheets;
  window.triggerLockAllTimesheets = triggerLockAllTimesheets;
  window.renderDashboard = renderDashboard;
}

export {
  autoConfirmAllEmployeesTimesheets,
  triggerLockAllTimesheets,
  renderDashboard
};
